import React, { useState } from 'react';
import { BarChart3, Users, DollarSign, ClipboardList, TrendingUp, Target, Folder } from 'lucide-react';
import { ReportConfig } from '../types';
import { useAuth } from '../utils/AuthContext';
import AppHeader from '../components/ui/AppHeader';
import ReportBuilder from '../components/reports/ReportBuilder';
import ReportPreview from '../components/reports/ReportPreview';
import { REPORT_TEMPLATES } from '../utils/reportTemplates';
import './ReportsPage.css';

export const ReportsPage: React.FC = () => {
  const { user } = useAuth();
  const [activeView, setActiveView] = useState<'templates' | 'builder' | 'preview'>('templates');
  const [selectedTemplate, setSelectedTemplate] = useState<ReportConfig | null>(null);
  const [currentConfig, setCurrentConfig] = useState<ReportConfig | null>(null);

  const handleSelectTemplate = (template: ReportConfig) => {
    const configWithUser: ReportConfig = {
      ...template,
      createdBy: user?.id || 'unknown',
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setSelectedTemplate(configWithUser);
    setCurrentConfig(configWithUser);
    setActiveView('builder');
  };

  const handleNewReport = () => {
    const newConfig: ReportConfig = {
      name: 'New Report',
      dataSource: 'leads',
      columns: [],
      filters: [],
      createdBy: user?.id || 'unknown',
      createdAt: new Date(),
      updatedAt: new Date(),
      isTemplate: false,
    };
    setSelectedTemplate(null);
    setCurrentConfig(newConfig);
    setActiveView('builder');
  };

  const handleConfigUpdate = (config: ReportConfig) => {
    setCurrentConfig(config);
  };

  const handlePreview = () => {
    if (currentConfig) {
      setActiveView('preview');
    }
  };

  const handleBackToBuilder = () => {
    setActiveView('builder');
  };

  const handleBackToTemplates = () => {
    setActiveView('templates');
    setSelectedTemplate(null);
    setCurrentConfig(null);
  };

  return (
    <div className="reports-page">
      <AppHeader />
      
      {/* Toolbar */}
      <div className="reports-toolbar">
        <div className="toolbar-left">
          <h1>Reports</h1>
          {activeView !== 'templates' && (
            <button className="back-btn" onClick={handleBackToTemplates}>
              ← Back to Templates
            </button>
          )}
        </div>
        <div className="toolbar-right">
          {activeView === 'templates' && (
            <button className="new-report-btn" onClick={handleNewReport}>
              + New Report
            </button>
          )}
          {activeView === 'builder' && currentConfig && (
            <button className="preview-btn" onClick={handlePreview}>
              Preview Report
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="reports-content">
        {activeView === 'templates' && (
          <div className="templates-view">
            <div className="templates-section">
              <h2><BarChart3 size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />Report Templates</h2>
              <p className="section-description">
                Start with a pre-built template and customize it to your needs
              </p>
              <div className="templates-grid">
                {REPORT_TEMPLATES.map(template => (
                  <div 
                    key={template.name}
                    className="template-card"
                    onClick={() => handleSelectTemplate(template)}
                  >
                    <div className="template-icon">
                      {template.dataSource === 'leads' && <Users size={24} />}
                      {template.dataSource === 'deals' && <DollarSign size={24} />}
                      {template.dataSource === 'activities' && <ClipboardList size={24} />}
                      {template.dataSource === 'pipeline-analytics' && <TrendingUp size={24} />}
                      {template.dataSource === 'conversion-metrics' && <Target size={24} />}
                    </div>
                    <div className="template-info">
                      <h3>{template.name}</h3>
                      <p>{template.description}</p>
                      <span className="template-source">{template.dataSource}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="custom-section">
              <h2><Folder size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />Custom Reports</h2>
              <p className="section-description">
                Build a custom report from scratch
              </p>
              <button className="create-custom-btn" onClick={handleNewReport}>
                + Create Custom Report
              </button>
            </div>
          </div>
        )}

        {activeView === 'builder' && currentConfig && (
          <ReportBuilder
            config={currentConfig}
            onConfigChange={handleConfigUpdate}
            onPreview={handlePreview}
          />
        )}

        {activeView === 'preview' && currentConfig && (
          <ReportPreview
            config={currentConfig}
            onBack={handleBackToBuilder}
          />
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
